import { fetchAPI } from './client';
import { toUazapiSendNumber } from '../../utils/phoneUtils';

export interface SendMessageOptions {
    quotedMessageId?: string;
    quotedPayload?: any;
    requestId?: string;
    onAccepted?: () => void;
    delay?: number; // Segundos (Uazapi v2 — Postman default: 0)
}

/** POST autenticado por instance — usado por todos os endpoints de envio. */
function postInstance(
    path: string,
    instanceName: string,
    payload: Record<string, unknown>,
    requestId?: string,
    onAccepted?: () => void,
): Promise<any> {
    return fetchAPI(path, {
        method: 'POST',
        onAccepted,
        headers: {
            'Content-Type': 'application/json',
            instance: instanceName,
            ...(requestId ? { "x-chat-request-id": requestId } : {}),
        },
        body: JSON.stringify(payload),
    });
}

/** replyid + quoted híbrido (preview em forks Baileys). */
function applyQuotedReply(
    payload: Record<string, any>,
    remoteJid: string,
    options?: SendMessageOptions,
): void {
    if (!options?.quotedMessageId) return;
    payload.replyid = options.quotedMessageId;

    if (!options.quotedPayload) return;
    const chatId =
        options.quotedPayload.remoteJid ||
        (remoteJid.includes('@') ? remoteJid : `${remoteJid}@s.whatsapp.net`);
    payload.quoted = {
        key: {
            remoteJid: chatId,
            fromMe: options.quotedPayload.fromMe,
            id: options.quotedPayload.id,
        },
        message: {
            conversation: options.quotedPayload.text || ' ',
        },
    };
}

function resolveMediaType(mimeType: string, mediaType?: string): string {
    if (mediaType === 'sticker') return 'sticker';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'ptt'; // push-to-talk / mensagem de voz
    return 'document';
}

/**
 * Envia uma mensagem de texto simples.
 * Endpoint: POST /send/text
 */
export async function sendText(
    instanceName: string,
    remoteJid: string,
    text: string,
    options?: SendMessageOptions,
): Promise<any> {
    const payload: Record<string, any> = {
        number: toUazapiSendNumber(remoteJid),
        text,
        linkPreview: false,
        delay: options?.delay ?? 0,
    };
    applyQuotedReply(payload, remoteJid, options);

    console.log('[Uazapi:sendText] 📤 Payload:', JSON.stringify(payload));
    return postInstance('send/text', instanceName, payload, options?.requestId, options?.onAccepted);
}

/**
 * Envia uma mensagem de mídia (Imagem, Vídeo, Áudio, Documento, Sticker).
 * Endpoint: POST /send/media
 */
export async function sendMedia(
    instanceName: string,
    remoteJid: string,
    mediaUrl: string,
    mimeType: string,
    caption?: string,
    options?: SendMessageOptions & { docName?: string; mediaType?: string },
): Promise<any> {
    const type = resolveMediaType(mimeType, options?.mediaType);

    const payload: Record<string, any> = {
        number: toUazapiSendNumber(remoteJid),
        file: mediaUrl,
        media: mediaUrl,
        type,
        mimetype: mimeType,
    };

    if (caption) payload.text = caption;
    if (type === 'document' && (options?.docName || caption)) {
        payload.docName = options?.docName || caption;
    }

    applyQuotedReply(payload, remoteJid, options);
    payload.delay = options?.delay ?? 0;

    console.log('[Uazapi:sendMedia] 📤 Payload:', JSON.stringify(payload));
    return postInstance('send/media', instanceName, payload, options?.requestId, options?.onAccepted);
}

/**
 * Envia uma reação (emoji) em uma mensagem existente.
 */
export async function sendReaction(
    instanceName: string,
    remoteJid: string,
    messageId: string,
    emoji: string,
): Promise<any> {
    return postInstance('send/reaction', instanceName, {
        number: toUazapiSendNumber(remoteJid),
        messageid: messageId,
        reaction: emoji,
    });
}

/**
 * Edita o texto de uma mensagem já enviada.
 * Endpoint: POST /message/edit
 */
export async function editMessage(
    instanceName: string,
    remoteJid: string,
    messageId: string,
    newText: string,
): Promise<any> {
    return postInstance('message/edit', instanceName, {
        number: toUazapiSendNumber(remoteJid),
        id: messageId,
        text: newText,
    });
}

/**
 * Exclui uma mensagem do chat.
 * Endpoint: POST /message/delete
 */
export async function deleteMessage(
    instanceName: string,
    remoteJid: string,
    messageId: string,
): Promise<any> {
    return postInstance('message/delete', instanceName, {
        number: toUazapiSendNumber(remoteJid),
        id: messageId,
    });
}

/** Tipos UAZAPI /send/pix-button — valores em minúsculo (n8n MessageDescription). */
export type PixKeyType = 'cpf' | 'cnpj' | 'phone' | 'email' | 'evp';

/**
 * Envia um cartão de contato.
 * Endpoint: POST /send/contact
 */
export async function sendContact(
    instanceName: string,
    remoteJid: string,
    contact: { fullName: string; phoneNumber: string; organization?: string; email?: string },
    options?: SendMessageOptions,
): Promise<any> {
    const payload: Record<string, any> = {
        number: toUazapiSendNumber(remoteJid),
        fullName: contact.fullName,
        phoneNumber: contact.phoneNumber,
        delay: options?.delay ?? 0,
    };
    if (contact.organization) payload.organization = contact.organization;
    if (contact.email) payload.email = contact.email;
    return postInstance('send/contact', instanceName, payload, options?.requestId, options?.onAccepted);
}

/**
 * Envia uma localização.
 * Endpoint: POST /send/location
 * Body UAZAPI: number, latitude, longitude, name, address (name/address obrigatórios na prática).
 */
export async function sendLocation(
    instanceName: string,
    remoteJid: string,
    location: { latitude: number; longitude: number; name?: string; address?: string },
    options?: SendMessageOptions,
): Promise<any> {
    const latitude = Number(location.latitude);
    const longitude = Number(location.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw new Error('Coordenadas de localização inválidas.');
    }

    const name = (location.name || 'Localização').trim() || 'Localização';
    const address =
        (location.address || location.name || `${latitude}, ${longitude}`)
            .replace(/\s*·\s*\d+([\.,]\d+)?\s*(m|km)\s*$/i, '')
            .trim() || `${latitude}, ${longitude}`;

    return postInstance('send/location', instanceName, {
        number: toUazapiSendNumber(remoteJid),
        latitude,
        longitude,
        name,
        address,
        delay: options?.delay ?? 0,
    }, options?.requestId, options?.onAccepted);
}

/**
 * Envia um botão PIX.
 * Endpoint: POST /send/pix-button
 * Body UAZAPI: number, pixKey, pixType (lowercase), pixName?
 */
export async function sendPixButton(
    instanceName: string,
    remoteJid: string,
    pix: { pixKey: string; type: PixKeyType; name?: string },
    options?: SendMessageOptions,
): Promise<any> {
    const payload: Record<string, any> = {
        number: toUazapiSendNumber(remoteJid),
        pixKey: pix.pixKey,
        pixType: pix.type,
        delay: options?.delay ?? 0,
    };
    if (pix.name) payload.pixName = pix.name;
    return postInstance('send/pix-button', instanceName, payload, options?.requestId, options?.onAccepted);
}
